import { useState, useEffect, useRef, useCallback } from "react";
import { AudioCache } from "../voice/audio-cache";

interface WaveformProps {
  messageId: string;
  voiceId: string;
  audioCache: AudioCache;
  isPlaying: boolean;
  onSeek: (messageId: string, fraction: number) => void;
  getPlaybackProgress: () => number;
}

const BAR_COUNT = 60;
const SVG_WIDTH = 140;
const SVG_HEIGHT = 24;
const BAR_WIDTH = 1.5;
const BAR_GAP = 0.8;
const BAR_STEP = BAR_WIDTH + BAR_GAP;
const PADDING_Y = 2;
const BAR_AREA = SVG_HEIGHT - PADDING_Y * 2;
const MIN_BAR_HEIGHT = 2;
const PEAK_INTERVAL_MS = 250;

export function Waveform({
  messageId,
  voiceId,
  audioCache,
  isPlaying,
  onSeek,
  getPlaybackProgress,
}: WaveformProps) {
  const [peaks, setPeaks] = useState<Float32Array>(() => new Float32Array(BAR_COUNT));
  const [progress, setProgress] = useState(0);

  const rafRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable ref so the rAF callback doesn't capture a stale prop
  const getProgressRef = useRef(getPlaybackProgress);
  getProgressRef.current = getPlaybackProgress;

  // Peak polling — runs while playing or while the cache entry is still streaming in
  const updatePeaks = useCallback(() => {
    const key = AudioCache.makeKey(messageId, voiceId);
    const updated = audioCache.getPeaks(key, BAR_COUNT);
    if (updated) setPeaks(updated);
  }, [messageId, voiceId, audioCache]);

  useEffect(() => {
    // Always do an immediate read on mount
    updatePeaks();

    const key = AudioCache.makeKey(messageId, voiceId);

    const shouldPoll = () => {
      // Keep polling if playing or if the entry isn't complete yet.
      // audioCache.has() returns true only when the entry is marked complete.
      return isPlaying || !audioCache.has(key);
    };

    if (shouldPoll()) {
      intervalRef.current = setInterval(() => {
        updatePeaks();
        if (!shouldPoll() && intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, PEAK_INTERVAL_MS);
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, messageId, voiceId, audioCache, updatePeaks]);

  // rAF loop for smooth playback progress — only runs while playing
  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const tick = () => {
      setProgress(getProgressRef.current());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying]);

  const handleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      onSeek(messageId, fraction);
    },
    [messageId, onSeek],
  );

  const cursorX = progress * SVG_WIDTH;

  return (
    <div className="waveform">
      <svg
        width={SVG_WIDTH}
        height={SVG_HEIGHT}
        onClick={handleClick}
        aria-hidden="true"
      >
        {Array.from(peaks).map((peak, i) => {
          const barHeight = Math.max(MIN_BAR_HEIGHT, peak * BAR_AREA);
          const x = i * BAR_STEP;
          const y = PADDING_Y + (BAR_AREA - barHeight) / 2;
          const played = x / SVG_WIDTH < progress;

          return (
            <rect
              key={i}
              className={played ? "waveform-bar-played" : "waveform-bar"}
              x={x}
              y={y}
              width={BAR_WIDTH}
              height={barHeight}
              rx={0.75}
            />
          );
        })}

        {isPlaying && (
          <line
            className="waveform-cursor"
            x1={cursorX}
            y1={0}
            x2={cursorX}
            y2={SVG_HEIGHT}
          />
        )}
      </svg>
    </div>
  );
}
